import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Switch } from '../ui/switch';
import { cn } from '../../lib/cn';
import {
  useCreateChannel,
  useUpdateChannel,
  useArchiveChannel,
  useDeleteChannel,
  type Channel,
} from '../../hooks/queries/useMessaging';

const EMOJI_OPTIONS = [
  { key: 'hashtag', label: 'Hashtag', svg: '/icons/hashtag.svg' },
  { key: 'rocket', label: 'Rocket', svg: '/icons/rocket.svg' },
  { key: 'lightbulb', label: 'Idée', svg: '/icons/lightbulb.svg' },
  { key: 'bullhorn', label: 'Annonce', svg: '/icons/bullhorn.svg' },
  { key: 'bug', label: 'Bug', svg: '/icons/bug.svg' },
  { key: 'code', label: 'Code', svg: '/icons/code.svg' },
  { key: 'palette', label: 'Design', svg: '/icons/palette.svg' },
  { key: 'chart-line', label: 'Stats', svg: '/icons/chart-line.svg' },
  { key: 'users', label: 'Équipe', svg: '/icons/users.svg' },
  { key: 'comments', label: 'Discussion', svg: '/icons/comments.svg' },
  { key: 'star', label: 'Favori', svg: '/icons/star.svg' },
  { key: 'heart', label: 'Favori', svg: '/icons/heart.svg' },
  { key: 'flag', label: 'Drapeau', svg: '/icons/flag.svg' },
  { key: 'bookmark', label: 'Marque-page', svg: '/icons/bookmark.svg' },
  { key: 'folder', label: 'Dossier', svg: '/icons/folder.svg' },
  { key: 'file', label: 'Fichier', svg: '/icons/file.svg' },
] as const;

type EmojiKey = typeof EMOJI_OPTIONS[number]['key'];

// FontAwesome SVGs are served from the public folder (symlinked or copied)
// Path: /icons/<name>.svg → served from apps/web/public/icons/
function emojiSvgPath(key: string): string {
  return `/icons/${key}.svg`;
}

interface ChannelSettingsModalProps {
  mode: 'create' | 'edit';
  channel?: Channel;
  onClose: () => void;
  onCreated?: (channelId: string) => void;
  onUpdated?: () => void;
  onDeleted?: () => void;
}

export function ChannelSettingsModal({
  mode,
  channel,
  onClose,
  onCreated,
  onUpdated,
  onDeleted,
}: ChannelSettingsModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [emoji, setEmoji] = useState<EmojiKey>('hashtag');
  const [isPrivate, setIsPrivate] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const createChannel = useCreateChannel();
  const updateChannel = useUpdateChannel();
  const archiveChannel = useArchiveChannel();
  const deleteChannel = useDeleteChannel();

  useEffect(() => {
    if (mode === 'edit' && channel) {
      setName(channel.name ?? '');
      setDescription(channel.description ?? '');
      setEmoji((channel.emoji as EmojiKey) ?? 'hashtag');
      setIsPrivate(channel.isPrivate);
    }
  }, [mode, channel]);

  const isPending =
    createChannel.isPending ||
    updateChannel.isPending ||
    archiveChannel.isPending ||
    deleteChannel.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;

    if (mode === 'create') {
      createChannel.mutate(
        { name: name.trim(), isPrivate, description: description.trim() || undefined, emoji },
        { onSuccess: (ch) => { onCreated?.(ch.id); onClose(); } },
      );
    } else if (channel) {
      updateChannel.mutate(
        { id: channel.id, params: { name: name.trim(), description: description.trim() || undefined, emoji, isPrivate } },
        { onSuccess: () => { onUpdated?.(); onClose(); } },
      );
    }
  }

  function handleArchive() {
    if (!channel) return;
    if (!window.confirm(`Archiver le canal #${channel.name} ? Il ne sera plus visible dans la sidebar.`)) return;
    archiveChannel.mutate(channel.id, {
      onSuccess: () => { onDeleted?.(); onClose(); },
    });
  }

  function handleDelete() {
    if (!channel) return;
    if (!window.confirm(`Supprimer définitivement le canal #${channel.name} et tous ses messages ? Cette action est irréversible.`)) return;
    deleteChannel.mutate(channel.id, {
      onSuccess: () => { onDeleted?.(); onClose(); },
    });
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-cime-surface rounded-xl shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-cime-border">
          <h2 className="font-heading text-lg font-semibold text-cime-text-primary">
            {mode === 'create' ? 'Créer un canal' : 'Paramètres du canal'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-cime-sidebar-active-bg text-cime-text-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Emoji + Nom */}
          <div className="flex items-start gap-3">
            {/* Emoji picker trigger */}
            <div className="relative flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowEmojiPicker((v) => !v)}
                className="w-11 h-11 rounded-lg border border-cime-border bg-cime-bg flex items-center justify-center hover:bg-cime-sidebar-active-bg transition-colors"
                title="Choisir une icône"
              >
                <img
                  src={emojiSvgPath(emoji)}
                  alt={emoji}
                  className="w-5 h-5 opacity-70"
                />
              </button>

              {showEmojiPicker && (
                <div className="absolute top-full left-0 mt-1 z-10 bg-cime-surface border border-cime-border rounded-xl shadow-lg p-2">
                  <div className="grid grid-cols-4 gap-1">
                    {EMOJI_OPTIONS.map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => { setEmoji(opt.key); setShowEmojiPicker(false); }}
                        title={opt.label}
                        className={cn(
                          'w-9 h-9 rounded-lg flex items-center justify-center transition-colors',
                          emoji === opt.key
                            ? 'bg-cime-primary/15 ring-1 ring-cime-primary'
                            : 'hover:bg-cime-sidebar-active-bg',
                        )}
                      >
                        <img src={emojiSvgPath(opt.key)} alt={opt.label} className="w-4 h-4 opacity-70" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Nom */}
            <div className="flex-1">
              <label className="block text-xs font-medium text-cime-text-muted mb-1">
                Nom du canal <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="ex: général, juridique, rh…"
                maxLength={50}
                required
                className="w-full text-sm bg-cime-bg border border-cime-border rounded-lg px-3 py-2 text-cime-text-primary placeholder:text-cime-text-muted focus:outline-none focus:ring-2 focus:ring-cime-primary/30 focus:border-cime-primary"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-cime-text-muted mb-1">
              Description <span className="text-cime-text-muted font-normal">(optionnel)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="À quoi sert ce canal ?"
              rows={2}
              className="w-full text-sm bg-cime-bg border border-cime-border rounded-lg px-3 py-2 text-cime-text-primary placeholder:text-cime-text-muted focus:outline-none focus:ring-2 focus:ring-cime-primary/30 focus:border-cime-primary resize-none"
            />
          </div>

          {/* Privé/Public */}
          <div className="flex items-center justify-between py-1">
            <div>
              <p className="text-sm font-medium text-cime-text-primary">Canal privé</p>
              <p className="text-xs text-cime-text-muted">
                {isPrivate ? 'Visible uniquement par les membres invités' : 'Visible par tous les collaborateurs'}
              </p>
            </div>
            <Switch checked={isPrivate} onCheckedChange={setIsPrivate} />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2 border-t border-cime-border">
            {mode === 'edit' && (
              <>
                <button
                  type="button"
                  onClick={handleArchive}
                  disabled={isPending}
                  className="text-xs text-amber-600 hover:text-amber-700 hover:underline disabled:opacity-50"
                >
                  Archiver
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isPending}
                  className="text-xs text-red-500 hover:text-red-600 hover:underline disabled:opacity-50"
                >
                  Supprimer
                </button>
              </>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-cime-text-muted hover:text-cime-text-primary transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isPending || !name.trim()}
              className="px-4 py-1.5 text-sm font-medium bg-cime-primary text-white rounded-lg hover:bg-cime-primary/90 disabled:opacity-50 transition-colors"
            >
              {mode === 'create' ? 'Créer' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
